import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { shopifyService } from "@/services/shopify";
import { Loader2, Settings } from "lucide-react";
import { ShopifyBlog, ShopifyIntegration } from "@/types/integration";

interface ShopifySettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  brandId: string;
  integration: ShopifyIntegration;
}

export function ShopifySettingsDialog({
  open,
  onOpenChange,
  onSuccess,
  brandId,
  integration,
}: ShopifySettingsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [availableBlogs, setAvailableBlogs] = useState<ShopifyBlog[]>([]);
  const [settings, setSettings] = useState({
    defaultBlogId: integration.settings?.defaultBlogId || "",
    autoPublish: integration.settings?.autoPublish ?? true,
    defaultAuthor: integration.settings?.defaultAuthor || "",
    defaultTags: integration.settings?.defaultTags || "",
  });

  useEffect(() => {
    if (open) {
      fetchBlogs();
    }
  }, [open]);

  const fetchBlogs = async () => {
    try {
      const data = await shopifyService.getBlogs(brandId);
      setAvailableBlogs(data.blogs);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await shopifyService.updateSettings({
        brandId,
        ...settings,
      });

      toast.success("Shopify settings updated successfully!");
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error("Error updating Shopify settings:", error);
      toast.error(error.message || "Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  const getSelectedBlog = () => {
    return availableBlogs.find(
      (blog) => blog.id.toString() === settings.defaultBlogId,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Shopify Integration Settings
          </DialogTitle>
          <DialogDescription>
            Update your Shopify publishing preferences and default values
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Store Info */}
          <div className="rounded-lg border p-4 bg-slate-50">
            <h4 className="font-medium mb-2">Connected Store</h4>
            <p className="text-sm text-muted-foreground">
              <strong>Store URL:</strong>{" "}
              {integration.credentials.url.replace("/admin", "")}
            </p>
          </div>

          {/* Blog Selection */}
          <div className="space-y-2">
            <Label htmlFor="blog-select">Default Blog</Label>
            <Select
              value={settings.defaultBlogId}
              onValueChange={(value) =>
                setSettings((prev) => ({ ...prev, defaultBlogId: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a blog for publishing articles" />
              </SelectTrigger>
              <SelectContent>
                {availableBlogs.map((blog) => (
                  <SelectItem key={blog.id} value={blog.id.toString()}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{blog.title}</span>
                      <span className="text-sm text-muted-foreground">
                        /{blog.handle}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Articles will be published to this blog by default
            </p>
          </div>

          {/* Publishing Settings */}
          <div className="space-y-4">
            <h4 className="font-medium">Publishing Settings</h4>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-publish">Auto-publish articles</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically publish articles to Shopify when they're created
                </p>
              </div>
              <Switch
                id="auto-publish"
                checked={settings.autoPublish}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({ ...prev, autoPublish: checked }))
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="default-author">Default Author</Label>
                <Input
                  id="default-author"
                  placeholder="e.g., Store Admin"
                  value={settings.defaultAuthor}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      defaultAuthor: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="default-tags">Default Tags</Label>
                <Input
                  id="default-tags"
                  placeholder="e.g., blog, news"
                  value={settings.defaultTags}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      defaultTags: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Current Settings Preview */}
          {settings.defaultBlogId && (
            <div className="rounded-lg border p-4 bg-blue-50">
              <h4 className="font-medium mb-2">Current Settings</h4>
              <div className="text-sm space-y-1">
                <p>
                  <strong>Default Blog:</strong> {getSelectedBlog()?.title}
                </p>
                <p>
                  <strong>Auto-publish:</strong>{" "}
                  {settings.autoPublish ? "Enabled" : "Disabled"}
                </p>
                {settings.defaultAuthor && (
                  <p>
                    <strong>Default Author:</strong> {settings.defaultAuthor}
                  </p>
                )}
                {settings.defaultTags && (
                  <p>
                    <strong>Default Tags:</strong> {settings.defaultTags}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={loading || !settings.defaultBlogId}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Settings"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ShopifySettingsButton({
  brandId,
  integration,
  onSuccess,
}: {
  brandId: string;
  integration: ShopifyIntegration;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="ml-2"
      >
        <Settings className="mr-1 h-3 w-3" />
        Settings
      </Button>

      <ShopifySettingsDialog
        open={open}
        onOpenChange={setOpen}
        onSuccess={onSuccess}
        brandId={brandId}
        integration={integration}
      />
    </>
  );
}
